// src/pages/StandingsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { onValue, ref } from "firebase/database";

const EMPTY_OBJ = {};

function computeRows(league) {
  const schedule    = league?.season?.schedule || EMPTY_OBJ;
  const currentWeek = Number(league?.season?.week || 1);
  const map = new Map();

  const ensure = (team) => {
    if (!team) return null;
    if (!map.has(team)) {
      map.set(team, { team, wins: 0, losses: 0, ties: 0, pf: 0, pa: 0, played: 0, streakType: "", streak: 0 });
    }
    return map.get(team);
  };

  Object.keys(schedule).map(Number).sort((a, b) => a - b).forEach(wk => {
    const week = schedule[String(wk)];
    const isComplete = wk < currentWeek; // only finalize W/L for past weeks
    (week?.matchups || []).forEach(m => {
      const home = m.home ?? null;
      const away = m.away ?? null;
      if (!home || !away) return;
      const a = ensure(home), b = ensure(away);
      if (!a || !b) return;
      const hp = Number(m.homePoints), ap = Number(m.awayPoints);
      if (!Number.isFinite(hp) || !Number.isFinite(ap)) return;
      // Always accumulate PF/PA so the scoreboard shows live points
      a.pf += hp; a.pa += ap;
      b.pf += ap; b.pa += hp;
      // Only count W/L/T and games played when the week is over
      if (!isComplete) return;
      a.played++; b.played++;
      if (hp > ap) {
        a.wins++;  b.losses++;
        a.streak = a.streakType === "W" ? a.streak + 1 : 1; a.streakType = "W";
        b.streak = b.streakType === "L" ? b.streak + 1 : 1; b.streakType = "L";
      } else if (ap > hp) {
        b.wins++;  a.losses++;
        b.streak = b.streakType === "W" ? b.streak + 1 : 1; b.streakType = "W";
        a.streak = a.streakType === "L" ? a.streak + 1 : 1; a.streakType = "L";
      } else {
        a.ties++; b.ties++;
        a.streak = a.streakType === "T" ? a.streak + 1 : 1; a.streakType = "T";
        b.streak = b.streakType === "T" ? b.streak + 1 : 1; b.streakType = "T";
      }
    });
  });

  return Array.from(map.values())
    .sort((a, b) => b.wins - a.wins || b.ties - a.ties || b.pf - a.pf || a.pa - b.pa)
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

const STREAK_COLOR = { W: "var(--green)", L: "var(--red)", T: "var(--text-dim)" };

export function StandingsPage({ leagueCode }) {
  const [league, setLeague] = useState(null);
  const [room,   setRoom]   = useState(null);

  useEffect(() => {
    const code = (leagueCode || "").trim().toUpperCase();
    if (!code) { setLeague(null); return; }
    const u1 = onValue(ref(db, `leagues/${code}`), snap => setLeague(snap.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   snap => setRoom(snap.val()   || null));
    return () => { u1(); u2(); };
  }, [leagueCode]);

  const rows = useMemo(() => computeRows(league), [league]);
  const playoffTeams = Number(league?.season?.config?.playoffTeams || 4);
  const playoffLine  = playoffTeams;
  const teamCustom   = room?.teamCustom || {};

  const getTeamDisplay = (slotId) => ({
    name:   teamCustom[slotId]?.name   || room?.slots?.[slotId]?.name || slotId,
    color:  teamCustom[slotId]?.color  || "var(--gold)",
    emoji:  teamCustom[slotId]?.emoji  || "🏏",
    imgUrl: teamCustom[slotId]?.imgUrl || null,
    abbr:   teamCustom[slotId]?.abbr   || slotId,
  });

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
            Standings
          </div>
          <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
            League: {leagueCode} · Top {playoffTeams} qualify for playoffs
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--text-dim)", lineHeight: 1.7 }}>
          No results yet. Once weekly scores are entered, standings will appear here.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Team</th>
                <th style={{ textAlign: "center" }}>W</th>
                <th style={{ textAlign: "center" }}>L</th>
                <th style={{ textAlign: "center" }}>T</th>
                <th style={{ textAlign: "center" }}>GP</th>
                <th style={{ textAlign: "right" }}>PF</th>
                <th style={{ textAlign: "right" }}>PA</th>
                <th style={{ textAlign: "center" }}>Streak</th>
                <th style={{ textAlign: "center" }}>Playoffs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const inPlayoffs  = r.rank <= playoffLine;
                const streakColor = STREAK_COLOR[r.streakType] || "var(--text-dim)";
                const isLastPlayoff = r.rank === playoffLine;
                return (
                  <React.Fragment key={r.team}>
                    <tr style={{ opacity: inPlayoffs ? 1 : 0.65 }}>
                      <td>
                        <div className={`rank-badge ${r.rank <= 3 ? `rank-${r.rank}` : ""}`}>
                          {r.rank}
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const d = getTeamDisplay(r.team);
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {d.imgUrl ? (
                                <img src={d.imgUrl} alt={d.name} style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                              ) : (
                                <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${d.color}22`, border: `1.5px solid ${d.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>
                                  {d.emoji}
                                </div>
                              )}
                              <div>
                                <div style={{ fontWeight: 700, color: d.color, fontSize: 13 }}>
                                  {d.name}
                                  {inPlayoffs && <span className="pill green" style={{ marginLeft: 6, fontSize: 8 }}>PO</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: "center", color: "var(--green)", fontWeight: 700 }}>{r.wins}</td>
                      <td style={{ textAlign: "center", color: "var(--red)" }}>{r.losses}</td>
                      <td style={{ textAlign: "center", color: "var(--text-dim)" }}>{r.ties}</td>
                      <td style={{ textAlign: "center", color: "var(--text-dim)" }}>{r.played}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{r.pf}</td>
                      <td style={{ textAlign: "right", color: "var(--text-dim)" }}>{r.pa}</td>
                      <td style={{ textAlign: "center" }}>
                        {r.streakType ? (
                          <span style={{ fontWeight: 700, color: streakColor }}>
                            {r.streakType}{r.streak}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {inPlayoffs
                          ? <span className="playoff-yes">✓</span>
                          : <span className="playoff-no">—</span>
                        }
                      </td>
                    </tr>
                    {isLastPlayoff && idx < rows.length - 1 && (
                      <tr>
                        <td colSpan={10} style={{ padding: "2px 12px" }}>
                          <div style={{ borderBottom: "1px dashed rgba(245,166,35,0.3)", fontSize: 9, color: "var(--gold)", textAlign: "center", letterSpacing: "0.1em" }}>
                            ── PLAYOFF LINE ──
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ padding: "10px 14px", fontSize: 11, color: "var(--text-muted)" }}>
        Sort: Wins → Ties → Points For → Points Against
      </div>
    </div>
  );
}
